import { Component, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { withSentry } from '../../utils/sentryLazy';

interface Props {
  children: ReactNode;
  fallbackTitle?: string;
}

interface State {
  hasError: boolean;
  message: string;
}

/**
 * ErrorBoundary — captura errores en tiempo de ejecución y muestra
 * un mensaje controlado en lugar de una pantalla blanca.
 *
 * Uso: wrappear cualquier página o sección crítica.
 */
export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, message: '' };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, message: error.message };
  }

  componentDidCatch(error: Error, info: { componentStack: string }) {
    console.error('[QuiPus ErrorBoundary]', error, info.componentStack);
    // Sentry es lazy (P-3) — withSentry encola si el SDK aún no cargó.
    withSentry((S) => S.captureException(error, { contexts: { react: { componentStack: info.componentStack } } }));
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[400px] p-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[var(--surface-raised)] border border-[var(--border)] mb-5">
            <AlertTriangle size={28} className="text-[var(--negative)]" />
          </div>
          <h2 className="text-lg font-bold mb-2" style={{ color: 'var(--color-foreground)' }}>
            {this.props.fallbackTitle ?? 'Algo salió mal'}
          </h2>
          <p className="text-sm mb-6 max-w-md" style={{ color: 'var(--color-muted-foreground)' }}>
            {this.state.message || 'Ocurrió un error inesperado en este módulo.'}
          </p>
          <button
            onClick={() => this.setState({ hasError: false, message: '' })}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[var(--brand)] text-white text-sm font-medium hover:bg-[var(--brand)] transition-colors"
          >
            <RefreshCw size={15} /> Reintentar
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
